import { type FormEventHandler, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { AuthSocialButtons } from "./AuthSocialButtons";
import type { SocialProvider } from "./SocialSignInButton";

const containerClasses =
  "flex flex-col gap-6 rounded-[28px] border border-slate-200 bg-white/95 p-8 shadow-2xl backdrop-blur-sm sm:p-10 dark:border-slate-800 dark:bg-slate-900/95";
const dividerClasses =
  "flex items-center gap-3 text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500";
const dividerLineClasses = "h-px flex-1 bg-slate-200 dark:bg-slate-700";
const headingTitleClasses = "text-2xl font-semibold text-slate-900 dark:text-slate-100";
const headingSubtitleClasses = "text-sm text-slate-500 dark:text-slate-400";
const formClasses = "flex flex-col gap-5";
const messageBaseClasses = "rounded-2xl border px-4 py-3 text-sm";
const errorMessageClasses =
  "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-900/50 dark:bg-rose-900/20 dark:text-rose-300";
const successMessageClasses =
  "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900/50 dark:bg-emerald-900/20 dark:text-emerald-300";

interface AuthFormBaseProps {
  children: ReactNode;
  onSubmit: FormEventHandler<HTMLFormElement>;
  submitButton: ReactNode;
  error?: string | null;
  success?: string | null;
  auxiliaryContent?: ReactNode;
  showSocialAuth?: boolean;
  socialDisabled?: boolean;
  socialButtonText?: Partial<Record<SocialProvider, string>>;
  className?: string;
  title?: string;
  subtitle?: string;
}

export function AuthFormBase({
  children,
  onSubmit,
  submitButton,
  error,
  success,
  auxiliaryContent,
  showSocialAuth = true,
  socialDisabled = false,
  socialButtonText,
  className,
  title,
  subtitle
}: AuthFormBaseProps) {
  const { t } = useTranslation();
  const wrapperClasses = [containerClasses, className].filter(Boolean).join(" ");

  return (
    <div className={wrapperClasses}>
      {title ? (
        <div className="flex flex-col gap-1">
          <h1 className={headingTitleClasses}>{title}</h1>
          {subtitle ? <p className={headingSubtitleClasses}>{subtitle}</p> : null}
        </div>
      ) : null}

      {showSocialAuth ? (
        <>
          <AuthSocialButtons disabled={socialDisabled} buttonText={socialButtonText} />

          <div className={dividerClasses} role="presentation">
            <span className={dividerLineClasses} />
            <span>{t("common.or_separator")}</span>
            <span className={dividerLineClasses} />
          </div>
        </>
      ) : null}

      <form onSubmit={onSubmit} noValidate className={formClasses}>
        {children}

        {auxiliaryContent}

        {error ? (
          <div className={`${messageBaseClasses} ${errorMessageClasses}`} role="alert">
            {error}
          </div>
        ) : null}

        {success ? (
          <div className={`${messageBaseClasses} ${successMessageClasses}`} role="status">
            {success}
          </div>
        ) : null}

        {submitButton}
      </form>
    </div>
  );
}
